import { useMemo, useRef, useState, useCallback, useEffect, useId } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import QuillTableBetter from 'quill-table-better';
import 'quill-table-better/dist/quill-table-better.css';
import { assetsApi } from '@/lib/api/assets';
import type { Asset } from '@/types/entities';
import { AssetPickerDialog } from '@/components/shared/asset-picker';
import { getAssetUrl, isImageAsset } from '@/lib/collection-field-utils';
import '@/styles/quill.css';

let isTableBetterRegistered = false;

function registerTableBetterModule() {
  if (isTableBetterRegistered) {
    return;
  }

  Quill.register({
    'modules/table-better': QuillTableBetter,
  }, true);

  isTableBetterRegistered = true;
}

registerTableBetterModule();

// Type for Quill Delta operations
interface DeltaOperation {
  insert?: string | Record<string, unknown>;
  delete?: number;
  retain?: number;
  attributes?: Record<string, unknown>;
}

// Quill Delta type - matches our storage format
export type QuillDelta = {
  ops: DeltaOperation[];
};

// Type guard to check if value is QuillDelta
function isQuillDelta(value: unknown): value is QuillDelta {
  return typeof value === 'object' && value !== null && 'ops' in value && Array.isArray((value as QuillDelta).ops);
}

interface QuillEditorProps {
  value: QuillDelta | string;
  onChange: (value: QuillDelta) => void;
  tenantSlug: string;
  disabled?: boolean;
  placeholder?: string;
  theme?: 'snow' | 'bubble';
  modules?: Record<string, unknown>;
  onImageSelect?: (imageUrl: string) => void;
}

type ColorFormat = 'color' | 'background';
type ActiveColorPicker = {
  format: ColorFormat;
  left: number;
};

function normalizeHexColor(value: string): string | null {
  const trimmed = value.trim();

  if (/^#[0-9a-f]{6}$/i.test(trimmed)) {
    return trimmed.toUpperCase();
  }

  if (/^#[0-9a-f]{3}$/i.test(trimmed)) {
    const [, r, g, b] = trimmed.toUpperCase();
    return `#${r}${r}${g}${g}${b}${b}`;
  }

  return null;
}

// Helper to normalize value to Delta object
function normalizeToDelta(value: QuillDelta | string): QuillDelta {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value) as QuillDelta;
      if (parsed.ops && Array.isArray(parsed.ops)) {
        return parsed;
      }
    } catch {
      // Not valid JSON, treat as plain text
      return { ops: [{ insert: value }] };
    }
  }
  if (!value || !isQuillDelta(value) || !value.ops) {
    return { ops: [{ insert: '' }] };
  }
  return value;
}

function hasTableBetterContent(delta: QuillDelta): boolean {
  return delta.ops.some((op) => {
    if (!op.attributes) {
      return false;
    }

    return Object.keys(op.attributes).some((key) => key.startsWith('table-'));
  });
}

function stringifyDeltaOps(delta: QuillDelta): string {
  const ops = delta.ops ?? [];
  if (ops.length === 1 && ops[0]?.insert === '') {
    return JSON.stringify([{ insert: '\n' }]);
  }
  return JSON.stringify(ops);
}

export function QuillEditor({
  value,
  onChange,
  tenantSlug,
  disabled = false,
  placeholder = 'Write content here...',
  theme = 'snow',
  modules = {},
  onImageSelect,
}: QuillEditorProps) {
  const quillRef = useRef<ReactQuill>(null);
  const toolbarId = useId().replace(/:/g, '');
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [cursorPosition, setCursorPosition] = useState<{ index: number } | null>(null);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [isAssetsLoading, setIsAssetsLoading] = useState(false);
  const [isAssetsUploading, setIsAssetsUploading] = useState(false);
  const [assetError, setAssetError] = useState<string | null>(null);
  const [textColor, setTextColor] = useState('#000000');
  const [backgroundColor, setBackgroundColor] = useState('#FFFFFF');
  const [textColorInput, setTextColorInput] = useState('#000000');
  const [backgroundColorInput, setBackgroundColorInput] = useState('#FFFFFF');
  const [activeColorPicker, setActiveColorPicker] = useState<ActiveColorPicker | null>(null);
  const selectedRangeRef = useRef<{ index: number; length: number } | null>(null);

  const fetchAssets = useCallback(async () => {
    if (!tenantSlug) return;

    setIsAssetsLoading(true);
    setAssetError(null);
    try {
      const data = await assetsApi.getAllByTenantSlug(tenantSlug);
      setAssets(data.filter((asset) => isImageAsset(asset.filepath)));
    } catch (err) {
      setAssetError(err instanceof Error ? err.message : 'Failed to load assets');
    } finally {
      setIsAssetsLoading(false);
    }
  }, [tenantSlug]);

  const handleAssetUpload = useCallback(async (nameInput: string, file: File) => {
    setIsAssetsUploading(true);
    setAssetError(null);
    try {
      await assetsApi.upload(tenantSlug, file, nameInput || undefined);
      await fetchAssets();
    } catch (err) {
      setAssetError(err instanceof Error ? err.message : 'Failed to upload asset');
      throw err;
    } finally {
      setIsAssetsUploading(false);
    }
  }, [fetchAssets, tenantSlug]);

  // Handle image selection from modal
  const handleImageSelect = useCallback((imageUrl: string) => {
    if (cursorPosition && quillRef.current) {
      const quill = quillRef.current.getEditor();
      quill.insertEmbed(cursorPosition.index, 'image', imageUrl, 'user');
      quill.setSelection(cursorPosition.index + 1);
    }
    onImageSelect?.(imageUrl);
  }, [cursorPosition, onImageSelect]);

  const handleAssetSelect = useCallback((assetId: number) => {
    const imageUrl = getAssetUrl(tenantSlug, assetId);
    handleImageSelect(imageUrl);
    setImageModalOpen(false);
  }, [handleImageSelect, tenantSlug]);

  // Configure modules with custom image handler
  const quillModules = useMemo(() => {
    const configModules: Record<string, unknown> = {
      ...modules,
    };

    configModules.toolbar = {
      container: `#${toolbarId}`,
    };

    configModules.table = false;
    configModules['table-better'] = {
      language: 'en_US',
      toolbarTable: true,
      ...(configModules['table-better'] as Record<string, unknown> ?? {}),
    };

    const keyboardConfig = (configModules.keyboard as Record<string, unknown> | undefined) ?? {};
    configModules.keyboard = {
      ...keyboardConfig,
      bindings: {
        ...((keyboardConfig.bindings as Record<string, unknown> | undefined) ?? {}),
        ...(QuillTableBetter.keyboardBindings as Record<string, unknown>),
      },
    };

    return configModules;
  }, [modules, toolbarId]);

  // Attach custom image handler after Quill initializes
  useEffect(() => {
    if (!tenantSlug || !quillRef.current) return;

    const quill = quillRef.current.getEditor();
    const toolbar = quill.getModule('toolbar') as any;

    if (toolbar) {
      toolbar.addHandler('image', () => {
        const range = quill.getSelection();
        setCursorPosition({ index: range?.index || 0 });
        setImageModalOpen(true);
      });
    }
  }, [tenantSlug]);

  useEffect(() => {
    if (!quillRef.current) {
      return;
    }

    const quill = quillRef.current.getEditor();

    const syncColorState = () => {
      const range = quill.getSelection();
      if (!range) {
        return;
      }

      const format = quill.getFormat(range);
      const nextTextColor = typeof format.color === 'string'
        ? normalizeHexColor(format.color)
        : null;
      const nextBackgroundColor = typeof format.background === 'string'
        ? normalizeHexColor(format.background)
        : null;

      if (nextTextColor) {
        setTextColor(nextTextColor);
        setTextColorInput(nextTextColor);
      }

      if (nextBackgroundColor) {
        setBackgroundColor(nextBackgroundColor);
        setBackgroundColorInput(nextBackgroundColor);
      }
    };

    quill.on('selection-change', syncColorState);
    quill.on('text-change', syncColorState);

    return () => {
      quill.off('selection-change', syncColorState);
      quill.off('text-change', syncColorState);
    };
  }, []);

  useEffect(() => {
    if (!quillRef.current) {
      return;
    }

    const quill = quillRef.current.getEditor();
    const toolbarElement = document.getElementById(toolbarId);
    if (!toolbarElement) {
      return;
    }

    const openColorPicker = (format: ColorFormat) => (event: Event) => {
      event.preventDefault();
      event.stopPropagation();

      const range = quill.getSelection();
      if (range) {
        selectedRangeRef.current = range;
      }

      const target = event.currentTarget as HTMLElement;
      const toolbarRect = toolbarElement.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();

      setActiveColorPicker({
        format,
        left: targetRect.left - toolbarRect.left,
      });
    };

    const colorLabel = toolbarElement.querySelector('.ql-color .ql-picker-label');
    const backgroundLabel = toolbarElement.querySelector('.ql-background .ql-picker-label');
    const handleColorClick = openColorPicker('color');
    const handleBackgroundClick = openColorPicker('background');

    colorLabel?.addEventListener('click', handleColorClick, true);
    backgroundLabel?.addEventListener('click', handleBackgroundClick, true);

    return () => {
      colorLabel?.removeEventListener('click', handleColorClick, true);
      backgroundLabel?.removeEventListener('click', handleBackgroundClick, true);
    };
  }, [toolbarId]);

  useEffect(() => {
    if (!activeColorPicker) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const toolbarElement = document.getElementById(toolbarId);
      if (!toolbarElement?.contains(event.target as Node)) {
        setActiveColorPicker(null);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [activeColorPicker, toolbarId]);

  useEffect(() => {
    if (!imageModalOpen) {
      return;
    }

    void fetchAssets();
  }, [fetchAssets, imageModalOpen]);

  // Normalize value to Delta for ReactQuill
  const quillValue = useMemo(() => {
    return normalizeToDelta(value);
  }, [value]);

  const hasTableBetterValue = useMemo(() => {
    return hasTableBetterContent(quillValue);
  }, [quillValue]);

  useEffect(() => {
    if (!quillRef.current) {
      return;
    }

    const quill = quillRef.current.getEditor();
    const currentValue = quill.getContents() as unknown as QuillDelta;

    if (stringifyDeltaOps(currentValue) === stringifyDeltaOps(quillValue)) {
      return;
    }

    if (hasTableBetterValue) {
      quill.setText('');
      quill.updateContents(quillValue as unknown as any, 'silent');
      return;
    }

    quill.setContents(quillValue as unknown as any, 'silent');
  }, [hasTableBetterValue, quillValue]);

  // Handle change - return Delta object
  const handleChange = (_content: string, _delta: any, source: string, editor: any) => {
    if (source === 'user') {
      // Get the full Delta from the editor
      const fullDelta = editor?.getContents?.();
      if (fullDelta) {
        onChange(fullDelta as unknown as QuillDelta);
      }
    }
  };

  const applyColor = useCallback((format: ColorFormat, value: string) => {
    const normalizedValue = normalizeHexColor(value);
    if (!normalizedValue || !quillRef.current) {
      return;
    }

    const quill = quillRef.current.getEditor();
    quill.focus();
    if (selectedRangeRef.current) {
      quill.setSelection(selectedRangeRef.current);
    }
    quill.format(format, normalizedValue, 'user');

    if (format === 'color') {
      setTextColor(normalizedValue);
      setTextColorInput(normalizedValue);
      return;
    }

    setBackgroundColor(normalizedValue);
    setBackgroundColorInput(normalizedValue);
  }, []);

  const clearColor = useCallback((format: ColorFormat) => {
    if (!quillRef.current) {
      return;
    }

    const quill = quillRef.current.getEditor();
    quill.focus();
    if (selectedRangeRef.current) {
      quill.setSelection(selectedRangeRef.current);
    }
    quill.format(format, false, 'user');
  }, []);

  return (
    <div className="quill-snow-wrapper relative z-0 overflow-visible rounded-md border border-input bg-background text-foreground shadow-xs focus-within:ring-2 focus-within:ring-ring/50">
      <div id={toolbarId}>
          <span className="ql-formats">
            <select className="ql-header" defaultValue="">
              <option value="1" />
              <option value="2" />
              <option value="3" />
              <option value="" />
            </select>
          </span>
          <span className="ql-formats">
            <button className="ql-bold" />
            <button className="ql-italic" />
            <button className="ql-underline" />
            <button className="ql-strike" />
          </span>
          <span className="ql-formats">
            <select className="ql-color" />
            <select className="ql-background" />
          </span>
          <span className="ql-formats">
            <button className="ql-blockquote" />
            <button className="ql-code-block" />
          </span>
          <span className="ql-formats">
            <button className="ql-list" value="ordered" />
            <button className="ql-list" value="bullet" />
          </span>
          <span className="ql-formats">
            <button className="ql-table-better" />
          </span>
          <span className="ql-formats">
            <button className="ql-link" />
            <button className="ql-image" />
          </span>
          <span className="ql-formats">
            <button className="ql-clean" />
          </span>
          {activeColorPicker && (
            <div
              className="quill-color-popover"
              style={{ left: activeColorPicker.left }}
            >
              <label className="quill-color-popover-row">
                <span>{activeColorPicker.format === 'color' ? 'Text color' : 'Background color'}</span>
                <input
                  type="color"
                  aria-label={activeColorPicker.format === 'color' ? 'Text color' : 'Background color'}
                  value={activeColorPicker.format === 'color' ? textColor : backgroundColor}
                  onChange={(event) => applyColor(activeColorPicker.format, event.target.value)}
                />
              </label>
              <label className="quill-color-popover-row">
                <span>Hex</span>
                <input
                  type="text"
                  aria-label={activeColorPicker.format === 'color' ? 'Text color hex code' : 'Background color hex code'}
                  value={activeColorPicker.format === 'color' ? textColorInput : backgroundColorInput}
                  onChange={(event) => {
                    if (activeColorPicker.format === 'color') {
                      setTextColorInput(event.target.value);
                      return;
                    }
                    setBackgroundColorInput(event.target.value);
                  }}
                  onBlur={() => {
                    applyColor(
                      activeColorPicker.format,
                      activeColorPicker.format === 'color' ? textColorInput : backgroundColorInput,
                    );
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      applyColor(
                        activeColorPicker.format,
                        activeColorPicker.format === 'color' ? textColorInput : backgroundColorInput,
                      );
                    }
                  }}
                />
              </label>
              <div className="quill-color-popover-actions">
                <button type="button" onClick={() => clearColor(activeColorPicker.format)}>
                  Clear
                </button>
                <button type="button" onClick={() => setActiveColorPicker(null)}>
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      <ReactQuill
        ref={quillRef}
        theme={theme}
        defaultValue=""
        onChange={handleChange as any}
        readOnly={disabled}
        placeholder={placeholder}
        modules={quillModules}
        style={{ minHeight: '200px' }}
      />
      {tenantSlug && (
        <AssetPickerDialog
          open={imageModalOpen}
          onOpenChange={setImageModalOpen}
          tenantSlug={tenantSlug}
          assets={assets}
          isLoading={isAssetsLoading}
          isUploading={isAssetsUploading}
          selectedAssetId={null}
          onSelectAsset={handleAssetSelect}
          onUpload={handleAssetUpload}
          error={assetError}
        />
      )}
    </div>
  );
}

// Helper to convert Delta to HTML for display
export function deltaToHtml(delta: QuillDelta | string): string {
  const tempDiv = document.createElement('div');
  const quill = new Quill(tempDiv);
  const normalizedDelta = normalizeToDelta(delta);

  // Convert QuillDelta to format expected by Quill
  const deltaOps = normalizedDelta.ops.map(op => {
    const staticOp: Record<string, unknown> = {};
    if ('insert' in op && op.insert !== undefined) {
      staticOp.insert = op.insert;
    }
    if ('delete' in op && op.delete !== undefined) {
      staticOp.delete = op.delete;
    }
    if ('retain' in op && op.retain !== undefined) {
      staticOp.retain = op.retain;
    }
    if (op.attributes) {
      (staticOp as any).attributes = op.attributes;
    }
    return staticOp;
  });

  quill.setContents(deltaOps as any);
  return quill.root.innerHTML;
}

// Helper to extract plain text from Delta
export function deltaToPlainText(delta: QuillDelta | string): string {
  const normalizedDelta = normalizeToDelta(delta);
  return normalizedDelta.ops
    ?.map(op => {
      if (typeof op.insert === 'string') {
        return op.insert;
      }
      return ''; // Skip non-text inserts (images, etc.)
    })
    .join('') || '';
}

// Export default
export { QuillEditor as default };
